import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link } from 'react-scroll';
import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWindows as fabFaWindows,
  faApple as fabFaApple,
  faLinux as fabFaLinux,
} from '@fortawesome/free-brands-svg-icons';

import FluidImg from 'Components/Utility/FluidImg';
import Drm from 'Components/Utility/Drm';

import 'Components/RedeemCode/RedeemCard.scss';
import 'Components/Gift/RedeemGiftProductCard.scss';

const RedeemGiftProductCard = ({
  product,
  shouldHighlight,
  switchCarouselGame,
  theme,
  unRevealedSlugs,
  setUnrevealedSlugs,
  recipientHasRedeemed,
}) => {
  const [revealed, setRevealed] = useState(false);
  const [letterZindex, setLetterZindex] = useState();

  const validatePlatforms = (drm) => {
    const validPlatforms = Object.keys(drm).filter(d => drm[d]);

    if (validPlatforms.length === 1) {
      return (
        <Drm drmType={validPlatforms[0]} colorOverride="dark" />
      );
    }
    return false;
  };

  const openTheGift = () => {
    setRevealed(true);
    window.setTimeout(() => {
      setLetterZindex(true);
    }, 1000);
  };

  const shouldReveal = () => {
    if (revealed) return false;
    setUnrevealedSlugs(unRevealedSlugs.filter(slug => product.slug !== slug));

    return openTheGift();
  };

  useEffect(() => {
    if (recipientHasRedeemed) openTheGift();
  }, [recipientHasRedeemed]);

  if (theme === 'chinesenewyear') {
    return (
      <button className="envelope-wrapper" onClick={shouldReveal}>
        <div className={`envelope ${revealed ? 'open' : 'closed'}`}>
          {!revealed &&
            <div className="click-to-reveal-container">
              <FormattedMessage id="redeemgiftproductcard.clicktoreveal" defaultMessage="Click to Reveal" />
            </div>
          }
          {!letterZindex &&
            <div className="front seal">
              <div className="golding">
                <img src="https://cdn.fanatical.com/production/assets/misc/chinese-new-year-letter-motif.png" alt="motif" />
              </div>
            </div>
          }
          <div className="front pocket" />
          <div className="letter">
            {revealed &&
              <div className="letter-contents">
                <Link
                  spy
                  smooth
                  offset={-120}
                  activeClass="active"
                  to="carousel"
                  duration={500}
                  className="faux-block-link__overlay-link w-100"
                  onClick={() => switchCarouselGame(product.totalGameIndex)}
                >
                  <div className={classNames('redeem-product-card', { 'sold-out': !product.regionAvailable }, { selected: shouldHighlight && product.regionAvailable }, { 'fade-out': !product.regionAvailable })}>
                    <div className="cover-container responsive-image-island">
                      <div className="card-border" />
                      <div className="responsive-image responsive-image--16by9">
                        <FluidImg
                          src={product.cover}
                          alt={product.name}
                          lazy={false}
                        />
                      </div>
                    </div>
                    <div className="card-content">
                      <div className="product-name">
                        {product.name}
                      </div>
                      <div className="product-icons-container">
                        {product.drm &&
                          <React.Fragment>
                            {validatePlatforms(product.drm)}
                          </React.Fragment>
                        }
                        <div className="os-icons-container">
                          {product.platforms && product.platforms.windows &&
                            <FontAwesomeIcon icon={fabFaWindows} className="ml-2" />
                          }
                          {product.platforms && product.platforms.mac &&
                            <FontAwesomeIcon icon={fabFaApple} className="ml-2" />
                          }
                          {product.platforms && product.platforms.linux &&
                            <FontAwesomeIcon icon={fabFaLinux} className="ml-2" />
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            }
          </div>
          <div className="left-motif">
            <img src="https://cdn.fanatical.com/production/assets/misc/chinese-new-year-letter-motif.png" alt="motif" />
          </div>
          <div className="right-motif">
            <img src="https://cdn.fanatical.com/production/assets/misc/chinese-new-year-letter-motif.png" alt="motif" />
          </div>
        </div>
      </button>
    );
  }

  return (
    <div className="gift-box-container">
      <button
        className={classNames('gift-box', {
          open: revealed,
          closed: !revealed,
          'letter-forward': letterZindex,
          birthday: theme === 'birthday',
          christmas: theme === 'christmas',
          standard: theme === 'standard' || !theme,
          thanksgiving: theme === 'thanksgiving',
        })}
        onClick={shouldReveal}
      >
        <div className="box">
          <div className="box-body">
            <div className="box-lid">
              <div className="box-bowtie" />
            </div>
            <div className="horizontal-stripe">
              <FormattedMessage id="redeemgiftproductcard.clicktoreveal" defaultMessage="Click to Reveal" />
            </div>
          </div>
          <div className="gift">
            <Link
              spy
              smooth
              offset={-120}
              activeClass="active"
              to="carousel"
              duration={500}
              className="faux-block-link__overlay-link w-100"
              onClick={() => switchCarouselGame(product.totalGameIndex)}
            >
              <div className={classNames('redeem-product-card', { 'sold-out': !product.regionAvailable }, { selected: shouldHighlight && product.regionAvailable }, { 'fade-out': !product.regionAvailable })}>
                <div className="cover-container responsive-image-island">
                  <div className="card-border" />
                  <div className="responsive-image responsive-image--16by9">
                    <FluidImg
                      src={product.cover}
                      alt={product.name}
                    />
                  </div>
                </div>
                <div className="card-content">
                  <div className="product-name">
                    {product.name}
                  </div>
                  <div className="product-icons-container">
                    {product.drm &&
                      <React.Fragment>
                        {validatePlatforms(product.drm)}
                      </React.Fragment>
                    }
                    <div className="os-icons-container">
                      {product.platforms && product.platforms.windows &&
                        <FontAwesomeIcon icon={fabFaWindows} className="ml-2" />
                      }
                      {product.platforms && product.platforms.mac &&
                        <FontAwesomeIcon icon={fabFaApple} className="ml-2" />
                      }
                      {product.platforms && product.platforms.linux &&
                        <FontAwesomeIcon icon={fabFaLinux} className="ml-2" />
                      }
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </button>
    </div>
  );
};

export default RedeemGiftProductCard;

RedeemGiftProductCard.propTypes = {
  product: PropTypes.shape({}),
  shouldHighlight: PropTypes.bool,
  switchCarouselGame: PropTypes.func,
  theme: PropTypes.string,
  unRevealedSlugs: PropTypes.arrayOf(PropTypes.slug),
  setUnrevealedSlugs: PropTypes.func,
  recipientHasRedeemed: PropTypes.bool,
};

RedeemGiftProductCard.defaultProps = {
  product: {},
  shouldHighlight: false,
  switchCarouselGame: () => {},
  theme: 'standard',
  setUnrevealedSlugs: () => {},
  unRevealedSlugs: [],
  recipientHasRedeemed: false,
};
